import classNames from "classnames";
import type { CSSProperties } from "react";
import type {
  V2ParEntry,
  V2ParImage,
  V2ParLink,
  V2ParText,
} from "src/generatorV2/types";

import styles from "./Par.module.scss";

interface ParProps {
  list: V2ParEntry[];
  layout?: "vertical" | "horizontal";
}

function isParText(entry: V2ParEntry): entry is V2ParText {
  return "text" in entry && !("image" in entry);
}

function isParLink(entry: V2ParEntry): entry is V2ParLink {
  return "link" in entry;
}

function isParImage(entry: V2ParEntry): entry is V2ParImage {
  return "image" in entry && entry.image;
}

function ParTextEntry({ entry }: { entry: V2ParText }) {
  const style: CSSProperties = {
    ...(entry.textAlign && { textAlign: entry.textAlign }),
  };
  const textClassName = classNames(
    styles.textEntry,
    entry.bold && styles.bold,
    entry.italic && styles.italic,
    entry.underline && styles.underline,
  );

  // Support <br> line breaks
  const parts = entry.text.split("<br>");
  return (
    <p className={textClassName} style={style}>
      {parts.map((part, i) => (
        <span key={`${part}-${i}`}>
          {i > 0 && <br />}
          {part}
        </span>
      ))}
    </p>
  );
}

function ParLinkEntry({ entry }: { entry: V2ParLink }) {
  return (
    <p className={styles.linkEntry}>
      <a
        href={entry.url}
        target={entry.target ?? "_self"}
        rel={entry.target === "_blank" ? "noopener noreferrer" : undefined}
        className={styles.link}>
        {entry.link}
      </a>
    </p>
  );
}

function ParImageEntry({ entry }: { entry: V2ParImage }) {
  const src = entry.url ?? entry.data;
  if (!src) return null;

  const style: CSSProperties = {
    ...(entry.width && { width: entry.width }),
    ...(entry.height && { height: entry.height }),
  };

  return (
    <div className={styles.imageEntry}>
      <img src={src} alt="" style={style} />
    </div>
  );
}

/**
 * Paragraph / rich-text block.
 * Renders a list of text, link, and image entries.
 */
export function Par({ list, layout = "vertical" }: ParProps) {
  return (
    <div
      className={classNames(
        styles.par,
        layout === "horizontal" ? styles.horizontal : styles.vertical,
      )}>
      {list.map((entry, i) => {
        const keyBase =
          (isParImage(entry) && (entry.url ?? entry.data)) ||
          (isParLink(entry) && `${entry.url}-${entry.link}`) ||
          (isParText(entry) && entry.text) ||
          "entry";
        const entryKey = `${keyBase}-${i}`;

        if (isParImage(entry))
          return <ParImageEntry key={entryKey} entry={entry} />;
        if (isParLink(entry))
          return <ParLinkEntry key={entryKey} entry={entry} />;
        if (isParText(entry))
          return <ParTextEntry key={entryKey} entry={entry} />;
        return null;
      })}
    </div>
  );
}
